import { Link } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { PrimaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { TEAM } from "@/lib/site";

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const TeamCard = ({ member, i }) => (
  <StaggerItem
    data-testid={`team-card-${i}`}
    className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-40px_rgba(6,15,34,0.5)]"
  >
    <div className="relative aspect-[4/5] overflow-hidden bg-navy">
      {member.photo ? (
        <img
          src={member.photo}
          alt={`${member.name}, ${member.role} at ASAI`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-navy to-navy-800" aria-label={`${member.name} photo placeholder`}>
          <span className="font-heading text-5xl font-bold text-white/25">{initials(member.name)}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-navy/60 to-transparent" />
    </div>
    <div className="flex flex-col gap-2 p-6">
      <h3 className="font-heading text-xl text-navy">{member.name}</h3>
      <span className="text-xs font-semibold uppercase tracking-wider text-royal">{member.role}</span>
      {(member.experience || member.previousRole) && (
        <div className="mt-1 flex flex-col gap-0.5 text-xs text-navy/55">
          {member.experience && <span><span className="font-semibold text-navy/75">Experience:</span> {member.experience}</span>}
          {member.previousRole && <span><span className="font-semibold text-navy/75">Previously:</span> {member.previousRole}</span>}
        </div>
      )}
      <p className="mt-1 text-sm text-navy/60">{member.desc}</p>
    </div>
  </StaggerItem>
);

export default function Team() {
  useSeo(
    "Our Team",
    "Meet the ASAI team — the coordinators, operations and social media professionals supporting industry-focused AI education and career development."
  );

  return (
    <div data-testid="page-team">
      <PageHeader
        overline="Our Team"
        titleLines={["The people", "powering ASAI"]}
        description="A dedicated team supporting our programs, operations and community — working to make industry-focused AI education accessible."
      />

      <section className="bg-white py-24 md:py-32">
        <div className="container-asai">
          <StaggerGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {TEAM.map((m, i) => (
              <TeamCard key={m.name} member={m} i={i} />
            ))}
          </StaggerGroup>
          <p className="mt-10 text-center text-xs text-navy/40">More team profiles and photos will be added soon.</p>
        </div>
      </section>

      <section className="bg-lightgrey py-20 md:py-24">
        <div className="container-asai flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <SectionHeading overline="Join us" title="Interested in working with ASAI?" description="We're always looking for people passionate about education and technology." />
          <div className="flex flex-wrap gap-3">
            <PrimaryButton to="/career" withArrow testId="team-careers-btn">View Careers</PrimaryButton>
            <Link to="/contact" data-testid="team-contact-btn" className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-7 py-3.5 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
